import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Search, Menu, X, Home as HomeIcon, Store, Sparkles, Phone, MessageSquare, User } from "lucide-react";
import { LOGO_URL, api } from "@/lib/api";
import { useLead } from "@/context/LeadDialog";
import { useAuth } from "@/context/AppContext";
import ReferrerBadge from "@/components/ReferrerBadge";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/shop", label: "Shop" },
  { to: "/product/daily-nutrition-spirulina-sea-buckthorn", label: "Daily Nutrition" },
  { to: "/ingredients", label: "Ingredients" },
  { to: "/about", label: "About" },
  { to: "/journal", label: "Journal" },
  { to: "/contact", label: "Contact" },
];

export default function Layout({ children }) {
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const { openLead } = useLead();
  const { user } = useAuth();
  const loc = useLocation();

  const runSearch = async (q) => {
    setSearch(q);
    if (!q) { setResults([]); return; }
    try { const { data } = await api.get(`/products?q=${encodeURIComponent(q)}`); setResults(data.slice(0, 6)); }
    catch { setResults([]); }
  };

  const hideChrome = loc.pathname.startsWith("/admin");

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--asure-ivory)" }}>
      {!hideChrome && (
        <>
          {/* announcement bar */}
          <div className="bg-asure-forest text-white text-[12px] tracking-wide">
            <div className="max-w-7xl mx-auto px-5 md:px-8 py-2 flex items-center justify-center gap-3 flex-wrap">
              <Sparkles size={12} style={{ color: "var(--asure-gold)" }} />
              <span>Launch offer · Introductory pricing on all ASURE products · Enquire for delivery to your city</span>
            </div>
          </div>

          <ReferrerBadge />

          <header className="sticky top-0 z-40 backdrop-blur-md bg-white/85 border-b border-[color:var(--asure-border)]">
            <div className="max-w-7xl mx-auto px-5 md:px-8 h-[76px] flex items-center justify-between gap-6">
              <Link to="/" data-testid="brand-logo" className="flex items-center gap-2">
                <img src={LOGO_URL} alt="ASURE" className="h-10 w-auto" />
              </Link>
              <nav className="hidden lg:flex items-center gap-7">
                {NAV.map((n) => (
                  <NavLink key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g,'-')}`}
                    className={({ isActive }) => `text-[13px] tracking-wide font-medium link-underline ${isActive ? 'text-asure-forest' : 'text-asure-text2'} hover:text-asure-forest`}>
                    {n.label}
                  </NavLink>
                ))}
              </nav>
              <div className="flex items-center gap-1 md:gap-3">
                <button onClick={() => setSearchOpen((v) => !v)} data-testid="nav-search-btn" aria-label="Search"
                  className="p-2 hover:text-asure-orange transition-colors"><Search size={18} /></button>
                <Link to={user ? "/account" : "/login"} data-testid="nav-account" aria-label="Account"
                  className="p-2 hover:text-asure-orange transition-colors">
                  {user?.picture ? <img src={user.picture} alt="" className="w-7 h-7 rounded-full border border-asure-forest/30 object-cover" /> : <User size={18} />}
                </Link>
                <button onClick={() => openLead()} className="hidden md:inline-flex btn-primary !py-2.5 !px-5 text-sm" data-testid="nav-enquire">I'm Interested</button>
                <button className="lg:hidden p-2" onClick={() => setOpen(true)} data-testid="nav-mobile-open"><Menu size={20} /></button>
              </div>
            </div>
            {searchOpen && (
              <div className="border-t border-[color:var(--asure-border)] bg-white/95">
                <div className="max-w-7xl mx-auto px-5 md:px-8 py-4">
                  <input autoFocus data-testid="search-input" value={search} onChange={(e) => runSearch(e.target.value)}
                    placeholder="Search Spirulina, Sea Buckthorn, Noni..."
                    className="w-full bg-transparent border-b border-asure-forest/30 py-3 text-lg font-display outline-none focus:border-asure-forest" />
                  {results.length > 0 && (
                    <div className="mt-4 grid md:grid-cols-2 gap-3">
                      {results.map((r) => (
                        <Link key={r.id} to={`/product/${r.slug}`} onClick={() => { setSearchOpen(false); setSearch(""); setResults([]); }}
                          className="flex items-center gap-3 p-3 rounded-lg hover:bg-asure-ivory">
                          <img src={r.images?.[0]} alt="" className="w-12 h-12 object-cover rounded" />
                          <div className="text-sm font-medium">{r.name}</div>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </header>
        </>
      )}

      {open && !hideChrome && (
        <div className="fixed inset-0 z-50 bg-black/40" onClick={() => setOpen(false)}>
          <aside className="absolute right-0 top-0 bottom-0 w-[86%] max-w-sm bg-white p-6 flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-8">
              <img src={LOGO_URL} alt="ASURE" className="h-9" />
              <button onClick={() => setOpen(false)} data-testid="nav-mobile-close"><X /></button>
            </div>
            <nav className="flex flex-col gap-4">
              {NAV.map((n) => (
                <Link key={n.to} to={n.to} onClick={() => setOpen(false)}
                  className="font-display text-2xl text-asure-text hover:text-asure-forest">{n.label}</Link>
              ))}
            </nav>
            <button onClick={() => { setOpen(false); openLead(); }} className="btn-primary mt-8" data-testid="mnav-enquire">I'm Interested</button>
          </aside>
        </div>
      )}

      <main className="flex-1">{children}</main>

      {!hideChrome && <Footer />}

      {!hideChrome && (
        <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 mobile-bottom-nav bg-white border-t border-[color:var(--asure-border)]">
          <div className="grid grid-cols-5 h-[60px]">
            <Link to="/" className="flex flex-col items-center justify-center gap-1 text-[10px]" data-testid="mbnav-home"><HomeIcon size={18} />Home</Link>
            <Link to="/shop" className="flex flex-col items-center justify-center gap-1 text-[10px]" data-testid="mbnav-shop"><Store size={18} />Shop</Link>
            <button onClick={() => setSearchOpen(true)} className="flex flex-col items-center justify-center gap-1 text-[10px]" data-testid="mbnav-search"><Search size={18} />Search</button>
            <Link to={user ? "/account" : "/login"} className="flex flex-col items-center justify-center gap-1 text-[10px]" data-testid="mbnav-account"><User size={18} />Account</Link>
            <button onClick={() => openLead()} className="flex flex-col items-center justify-center gap-1 text-[10px] text-asure-orange" data-testid="mbnav-enquire"><MessageSquare size={18} />Enquire</button>
          </div>
        </nav>
      )}
    </div>
  );
}

function Footer() {
  return (
    <footer className="mt-24 text-asure-ivory" style={{ background: "var(--asure-forest)" }}>
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-16 grid gap-10 md:grid-cols-5">
        <div className="md:col-span-2">
          <img src={LOGO_URL} alt="ASURE" className="h-12 bg-white/95 rounded-xl p-2 w-fit" />
          <div className="mt-4 eyebrow" style={{ color: "var(--asure-gold)" }}>Better Every Day.</div>
          <p className="mt-4 text-sm text-white/80 max-w-sm">A modern Indian nutrition and wellness brand focused on thoughtfully selected ingredients and simple daily routines.</p>
          <p className="mt-6 text-xs text-white/60 leading-relaxed">
            ASURE OPC PRIVATE LIMITED<br/>
            Asure Building, Sewai, Nahar Road<br/>
            Sushant Golf City, Lucknow, Uttar Pradesh, India
          </p>
          <div className="mt-4 flex items-center gap-2 text-xs text-white/70"><Phone size={12} /><a href="tel:+918269784515" className="hover:text-white">+91 82697 84515</a></div>
        </div>
        <FCol title="Shop" links={[["/shop","All Product"],["/product/daily-nutrition-spirulina-sea-buckthorn","Daily Nutrition"],["/ingredients","Ingredients"],["/journal","Journal"]]} />
        <FCol title="Company" links={[["/about","About"],["/quality","Quality"],["/contact","Contact"],["/faq","FAQ"]]} />
        <FCol title="Follow" links={[["https://www.instagram.com/asurelife/","Instagram"],["#","Facebook"],["#","YouTube"],["#","LinkedIn"]]} />
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-6 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-white/60">
          <div>© {new Date().getFullYear()} ASURE OPC PRIVATE LIMITED. All rights reserved.</div>
          <div>Nutraceutical · Not for medicinal use · Not intended to diagnose, treat, cure or prevent any disease.</div>
        </div>
      </div>
    </footer>
  );
}
function FCol({ title, links }) {
  return (
    <div>
      <div className="eyebrow" style={{ color: "var(--asure-gold)" }}>{title}</div>
      <ul className="mt-4 space-y-2">
        {links.map(([to, l]) => {
          const external = to.startsWith("http");
          return (
            <li key={l}>
              {external ? (
                <a href={to} target="_blank" rel="noopener noreferrer" className="text-sm text-white/85 hover:text-white">{l}</a>
              ) : (
                <Link to={to} className="text-sm text-white/85 hover:text-white">{l}</Link>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
